import { useCallback } from "react";

import debugLogger from "src/platform/utils/debugLogger";
import {
  ErrorSource,
  ErrorSeverity,
  createAppError,
} from "src/platform/utils/errorHandler";
import { useNotification } from "src/shell/contexts/notificationContext";

interface ErrorHandlerOptions {
  /**
   * Whether to show a notification for this error
   * Default: true
   */
  showNotification?: boolean;

  /**
   * Whether to log the error
   * Default: true
   */
  logError?: boolean;

  /**
   * Custom error message to display instead of the error's message
   */
  customMessage?: string;

  /**
   * Additional metadata to include with the error
   */
  metadata?: Record<string, unknown>;

  /**
   * Context for logging
   */
  context?: string;
}

interface CreateErrorOptions {
  /**
   * Whether to show a notification for this error
   * Default: true
   */
  showNotification?: boolean;

  /**
   * Whether to log the error
   * Default: true
   */
  logError?: boolean;

  /**
   * Additional metadata to include with the error
   */
  metadata?: Record<string, unknown>;

  /**
   * Context for logging
   */
  context?: string;
}

/**
 * Custom hook for handling errors consistently across the application
 * Provides standardized error handling with optional notification and logging
 */
export const useErrorHandler = () => {
  const { setSnackBarNotification } = useNotification();

  /**
   * Standardized error handler
   * @param error Any error object
   * @param options Options for handling the error
   */
  const handleError = useCallback(
    (error: unknown, options: ErrorHandlerOptions = {}) => {
      const {
        showNotification: shouldShowNotification = true,
        logError = true,
        context = "Application",
      } = options;

      // Convert error to string message
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : "An unknown error occurred";

      // Log error if enabled
      if (logError) {
        debugLogger.error(`${context} Error:`, error);
      }

      // Show notification if enabled
      if (shouldShowNotification) {
        setSnackBarNotification({
          message: errorMessage,
          severity: "error",
        });
      }

      return errorMessage;
    },
    [setSnackBarNotification],
  );

  /**
   * Create and handle a new error
   * @param message Error message
   * @param source Error source
   * @param severity Error severity
   * @param originalError Original error object
   * @param options Options for handling the error
   */
  const createError = useCallback(
    (
      message: string,
      source: ErrorSource = ErrorSource.APPLICATION,
      severity: ErrorSeverity = ErrorSeverity.ERROR,
      originalError?: unknown,
      options: CreateErrorOptions = {},
    ) => {
      const {
        showNotification = true,
        logError = true,
        metadata,
        context = "Application",
      } = options;

      const appError = createAppError(
        message,
        source,
        severity,
        originalError,
        metadata,
      );

      // Log error if enabled
      if (logError) {
        debugLogger.error(`${context} Error:`, appError);
      }

      // Show notification if enabled
      if (showNotification) {
        setSnackBarNotification({
          message: appError.message,
          severity:
            severity === ErrorSeverity.INFO
              ? "info"
              : severity === ErrorSeverity.WARNING
                ? "warning"
                : "error",
        });
      }

      return appError;
    },
    [setSnackBarNotification],
  );

  return {
    handleError,
    createError,
  };
};
